import { logExploreFiltersApplied, logExploreMapPanEnd } from '@alltrails/analytics/amplitudeFunctions';
import {
  SearchDifficultyFilter,
  SearchOrigin,
  SearchRouteTypeFilter,
  SearchTrailCompletedFilter,
  SearchTrailTrafficFilter
} from '@alltrails/analytics/amplitudeEnums';
import { Filters } from 'types/Search';
import { MapStatsUtil } from './map_stats_util';
import { SearchFiltersUtil } from './search_filters_util';
import { GeoLocation } from '../types/Location/GeoLocation';
import { Trail } from '../types/Trails/Trail';
import { Track } from '../types/Trails/Track';
import { FILTER, MAP_PAN } from './constants/SearchAnalyticsConstants';

const logFiltersApplied = (
  filters: Filters,
  numResults: number,
  resultsLimit: number,
  center: GeoLocation | null,
  searchRadius: number,
  source: SearchOrigin
): void => {
  const activityFilters = SearchFiltersUtil.getSelectedActivityFilterValues(filters);
  const difficultyFilters = SearchFiltersUtil.getSelectedDifficultyFilterValues(filters);
  const attractionFilters = SearchFiltersUtil.getSelectedAttractionsFilterValues(filters);
  const suitabilityFilters = SearchFiltersUtil.getSelectedSuitabilityFilterValues(filters);
  const routeTypeFilters = SearchFiltersUtil.getSelectedRouteFilterValues(filters);
  const trailTrafficFilters = SearchFiltersUtil.getSelectedTrailTrafficFilterValues(filters);
  const completionFilters = SearchFiltersUtil.getSelectedCompletedFilterValues(filters);
  logExploreFiltersApplied({
    activity_type_filters: activityFilters.length > 0 ? JSON.stringify(activityFilters) : null,
    attractions_filters: attractionFilters.length ? attractionFilters : null,
    difficulty_filters: JSON.stringify(difficultyFilters.length ? difficultyFilters : null) as SearchDifficultyFilter,
    elevation_filters: JSON.stringify(
      SearchFiltersUtil.hasAlteredSliderFilter(filters.elevation_gain)
        ? {
            min: MapStatsUtil.metersToFeet(filters.elevation_gain.value[0]),
            max: MapStatsUtil.metersToFeet(filters.elevation_gain.value[1])
          }
        : null
    ),
    length_filters: JSON.stringify(
      SearchFiltersUtil.hasAlteredSliderFilter(filters.lengths)
        ? {
            min: MapStatsUtil.metersToMiles(filters.lengths.value[0]),
            max: MapStatsUtil.metersToMiles(filters.lengths.value[1])
          }
        : null
    ),
    limit_filters: resultsLimit,
    location_filters: JSON.stringify(
      center != null || searchRadius != null
        ? {
            lat: center?.lat ?? null,
            lng: center?.lng ?? null,
            radius: searchRadius
          }
        : null
    ),
    num_filters_selected: SearchFiltersUtil.getNumSelectedFilters(filters),
    num_results_returned: numResults,
    rating_filters: parseFloat(filters.minRating),
    route_type_filters: JSON.stringify(routeTypeFilters.length ? routeTypeFilters : null) as SearchRouteTypeFilter, // JSON array of enum values
    search_origin: source,
    sort_filters: SearchFiltersUtil.getSelectedSortFilter(filters),
    suitability_filters: suitabilityFilters.length ? suitabilityFilters : null,
    trail_completion_filters: JSON.stringify(completionFilters.length ? completionFilters : null) as SearchTrailCompletedFilter, // JSON array of enum values
    trail_traffic_filters: JSON.stringify(trailTrafficFilters.length ? trailTrafficFilters : null) as SearchTrailTrafficFilter // JSON array of enum values
  });
};

const logSearchResultsAnalytics = (
  filters: Filters,
  results: Trail[] | Track[],
  resultsLimit: number,
  center: number[],
  searchRadius: number,
  source: SearchOrigin,
  searchTrigger: string
) => {
  const centerCoords = center ? { lat: center[0], lng: center[1] } : null;
  switch (searchTrigger) {
    case MAP_PAN:
      logExploreMapPanEnd({ lat: centerCoords.lat, lng: centerCoords.lng, num_results_returned: results.length, zoom_level: filters.zoomLevel });
      break;
    case FILTER:
      logFiltersApplied(filters, results.length, resultsLimit, centerCoords, searchRadius, source);
      break;
    default:
    // Do nothing - no other search analytics to log
  }
};

export { logSearchResultsAnalytics };
